import { useMemo } from 'react'
import { useOutletContext } from 'react-router-dom'
import type { ModerationOverviewStats } from '../../api/moderation'
import { mapActionKeyToLabel, mapContentTypeToLabel, mapQueueKeyToLabel } from '../../lib/moderationLabels'

type ModerationStatsContext = {
  stats: ModerationOverviewStats | null
  loading: boolean
  error: string | null
}

function formatCount(value?: number | null) {
  if (value === null || value === undefined) return '0'
  return value.toLocaleString()
}

function KpiCard({
  label,
  lines,
  hint,
}: {
  label: string
  lines: Array<{ label: string; value: string }>
  hint?: string
}) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/5 p-4">
      <div className="text-xs uppercase tracking-wide text-white/50">{label}</div>
      <div className="mt-3 space-y-1 text-sm text-white/80">
        {lines.map((line) => (
          <div key={line.label} className="flex items-center justify-between gap-3">
            <span className="text-white/60">{line.label}</span>
            <span className="font-semibold text-white">{line.value}</span>
          </div>
        ))}
      </div>
      {hint && <div className="mt-1 text-xs text-white/60">{hint}</div>}
    </div>
  )
}

function BarList({
  title,
  items,
  labelFor,
}: {
  title: string
  items: Array<{ key: string; count: number }>
  labelFor?: (key: string) => string
}) {
  const max = Math.max(1, ...items.map((item) => item.count))
  return (
    <div className="rounded-xl border border-white/10 bg-white/5 p-4">
      <div className="text-sm font-semibold">{title}</div>
      {items.length === 0 ? (
        <div className="mt-3 text-xs text-white/60">No data yet.</div>
      ) : (
        <div className="mt-3 space-y-3">
          {items.map((item) => {
            const label = labelFor ? labelFor(item.key) : item.key
            const width = `${Math.round((item.count / max) * 100)}%`
            return (
              <div key={item.key}>
                <div className="flex items-center justify-between text-xs text-white/80">
                  <span className="font-semibold">{label}</span>
                  <span>{item.count}</span>
                </div>
                <div className="mt-1 h-2 rounded-full bg-white/10">
                  <div className="h-2 rounded-full bg-brand/60" style={{ width }} />
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

function TopList({
  title,
  items,
  renderLabel,
  showDevTip,
}: {
  title: string
  items: Array<{ label: string; count: number }>
  renderLabel?: (label: string) => React.ReactNode
  showDevTip?: boolean
}) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/5 p-4">
      <div className="text-sm font-semibold">{title}</div>
      {items.length === 0 ? (
        <div className="mt-3 text-xs text-white/60">
          <div>No activity in the last 24h.</div>
          {showDevTip && (
            <div className="mt-1 text-[11px] text-white/45">
              Tip: run python manage.py moderation_smoke_test --force-escalate to generate sample events.
            </div>
          )}
        </div>
      ) : (
        <div className="mt-3 space-y-2 text-xs text-white/80">
          {items.map((item) => (
            <div key={item.label} className="flex items-center justify-between gap-2">
              <span className="truncate">{renderLabel ? renderLabel(item.label) : item.label}</span>
              <span className="text-white/60">{item.count}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default function ModerationOverview() {
  const context = useOutletContext<ModerationStatsContext | null>() || {
    stats: null,
    loading: false,
    error: null,
  }
  const { stats, loading, error } = context

  const kpis = stats?.kpis
  const contentTypeItems = useMemo(() => stats?.by_content_type || [], [stats])
  const actionItems = useMemo(() => stats?.by_action || [], [stats])
  const queueItems = useMemo(() => stats?.by_queue || [], [stats])

  const topRules = useMemo(
    () => (stats?.top_rules_24h || []).map((item) => ({ label: item.id, count: item.count })),
    [stats]
  )
  const topDomains = useMemo(
    () => (stats?.top_domains_24h || []).map((item) => ({ label: item.domain, count: item.count })),
    [stats]
  )
  const topActors = useMemo(
    () =>
      (stats?.top_actors_24h || []).map((item) => ({
        label: item.username || `User ${item.actor_id}`,
        count: item.count,
      })),
    [stats]
  )

  const urgentInflow = queueItems.find((item) => item.key === 'urgent')?.count ?? 0

  if (loading) {
    return <div className="rounded-xl border border-white/10 p-4 text-white/70">Loading...</div>
  }

  if (error) {
    return <div className="rounded-xl border border-red-400/40 bg-red-500/10 p-4 text-red-100">{error}</div>
  }

  if (!stats || !kpis) {
    return <div className="rounded-xl border border-white/10 p-4 text-white/70">No data available.</div>
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <KpiCard
          label="Total events"
          lines={[
            { label: '24h', value: formatCount(kpis.events_24h) },
            { label: '7d', value: formatCount(kpis.events_7d) },
          ]}
        />
        <KpiCard
          label="AI decided"
          lines={[
            { label: '7d', value: formatCount(kpis.ai_decided_7d) },
          ]}
        />
        <KpiCard
          label="Sent to human"
          lines={[
            { label: '7d', value: formatCount(kpis.sent_to_human_7d) },
          ]}
        />
        <KpiCard
          label="Human resolved"
          lines={[
            { label: '7d', value: formatCount(kpis.human_resolved_7d) },
          ]}
        />
        <KpiCard
          label="Reports submitted"
          lines={[
            { label: '24h', value: formatCount(kpis.reports_24h) },
            { label: '7d', value: formatCount(kpis.reports_7d) },
          ]}
        />
        <KpiCard
          label="Appeals submitted"
          lines={[
            { label: '24h', value: formatCount(kpis.appeals_24h) },
            { label: '7d', value: formatCount(kpis.appeals_7d) },
          ]}
        />
      </div>

      {(kpis.sent_to_human_7d || kpis.human_resolved_7d || urgentInflow) ? (
        <div className="rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white/80">
          This week: Sent to human {formatCount(kpis.sent_to_human_7d)} (urgent {formatCount(urgentInflow)}) · Human resolved {formatCount(kpis.human_resolved_7d)}
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <BarList
          title="Events by content type (7d)"
          items={contentTypeItems}
          labelFor={mapContentTypeToLabel}
        />
        <BarList
          title="Events by action (7d)"
          items={actionItems}
          labelFor={mapActionKeyToLabel}
        />
        <BarList
          title="Queue split (7d)"
          items={queueItems}
          labelFor={mapQueueKeyToLabel}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <TopList title="Top rules (24h)" items={topRules} showDevTip={import.meta.env.DEV} />
        <TopList title="Top domains (24h)" items={topDomains} showDevTip={import.meta.env.DEV} />
        <TopList title="Top actors (24h)" items={topActors} showDevTip={import.meta.env.DEV} />
      </div>
    </div>
  )
}
